import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { lmsAdmin } from '../api'
import { dateTimeText, utcText } from '../../../shared/date/local-time'
import { LmsIcon } from '../lms-icon'

// The courses whose current package was built before the automatic launch
// (Phase F Task 8; lms D-108: the connection's diagnostics list them as
// packages_to_rebuild, oldest build first, at most 50, with the reason
// "rebuild for automatic launch"). Such a package still launches, through the
// old page addresses' redirects, but not by itself: its admin builds it again
// in EducationXR and uploads the new one to the LMS. The rebuild is the course
// page's own (POST the build for the course on this connection); a rebuilt
// package is not in the LMS until it is uploaded there, so the page says so.
// Drawn only when there is something to say.

const FORMAT = { 'scorm-1.2': 'SCORM 1.2', 'scorm-2004-4ed': 'SCORM 2004', cmi5: 'cmi5' }

const sentence = text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '')

const were = names => (names.length === 1 ? `${names[0]} was rebuilt` : `${names.join(', ')} were rebuilt`)

// `readOnly`: the connection can no longer change (archived, or a role that may only read): the list is
// shown and nothing is rebuilt from it.
export const PackagesToRebuild = ({ connectionId, packages, reload, readOnly }) => {
  const [building, updateBuilding] = useState(null)
  const [error, updateError] = useState(null)
  const [rebuilt, updateRebuilt] = useState([])
  // Set before the page redraws: a second press (a double click) builds nothing.
  const running = useRef(false)

  const rebuild = async entry => {
    if (running.current) return
    running.current = true
    updateBuilding(entry.node_id)
    updateError(null)
    try {
      await lmsAdmin.buildPackage(connectionId, entry.node_id)
      updateRebuilt(previous => (previous.includes(entry.node_name) ? previous : [...previous, entry.node_name]))
      await reload()
    } catch (e) {
      updateError(e)
    } finally {
      running.current = false
      updateBuilding(null)
    }
  }

  const list = Array.isArray(packages) ? packages : []

  return (
    <React.Fragment>
      {list.length > 0 && (
        <div className="form-section" data-testid="packages-to-rebuild">
          <div className="form-section-title">
            <span className="num">
              <LmsIcon name="rotate" style={{ fontSize: 9 }} />
            </span>
            <h2 className="label">Packages to rebuild</h2>
          </div>
          <div className="field">
            <div className="field-help" style={{ marginTop: 0, marginBottom: 6 }}>
              These courses' packages were built before launches opened automatically. They still launch, but not by
              themselves:{' '}
              {readOnly
                ? "they can't be rebuilt while this connection can't be changed."
                : 'rebuild each one, then upload the new package to your LMS.'}
            </div>
            <ul className="list-unstyled mb-0">
              {list.map(entry => (
                <li key={entry.package_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ overflowWrap: 'anywhere' }}>
                      <Link to={`/org/lms/courses/${entry.node_id}/integration`}>{entry.node_name}</Link>
                      {entry.format && <span className="field-help"> · {FORMAT[entry.format] || entry.format}</span>}
                    </div>
                    <div className="field-help" style={{ marginTop: 0 }}>
                      {'Built '}
                      <span title={utcText(entry.built_at)}>{dateTimeText(entry.built_at)}</span>
                      {entry.reason && ` · ${sentence(entry.reason)}`}
                    </div>
                  </div>
                  {!readOnly && (
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      type="button"
                      aria-label={`Rebuild ${entry.node_name}`}
                      disabled={building !== null}
                      onClick={() => rebuild(entry)}
                    >
                      {building === entry.node_id ? 'Rebuilding…' : 'Rebuild'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {error && (
              <div className="field-error" data-testid="packages-to-rebuild-error">
                <LmsIcon name="circle-exclamation" />
                {error.message}
              </div>
            )}
          </div>
        </div>
      )}
      {rebuilt.length > 0 && (
        <div className="form-section" data-testid="packages-rebuilt">
          <div className="field">
            <div className="field-help" style={{ marginTop: 0 }}>
              <LmsIcon name="circle-check" /> {were(rebuilt)}. Download the new package from the course's page and upload it
              to your LMS: until you do, your LMS still launches the old one.
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  )
}
